import {
  CommissionAccompanimentData,
  CommissionAccompanimentItem,
} from "@/types/commissionAccompaniment";
import { SortableTable, SortableColumn } from "@/components/SortableTable";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import {
  formatCurrency,
  formatPercent,
  exportToCSV,
} from "@/utils/commissionReportUtils";
import { Mask } from "@/utils/mask";
import { updateCommissionConfigData } from "@/Api/commissionConfig";
import { toast } from "sonner";

type TableDataProps = {
  data: CommissionAccompanimentData;
};

export const TableData = ({ data }: TableDataProps) => {
  const [globalDiscount, setGlobalDiscount] = useState<string>(
    Mask.formatCurrency(data?.commission?.manual_discount_value || 0)
  );
  const [loading, setLoading] = useState(false);

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalDiscount(e.target.value);
  };

  const handleApplyDiscount = async () => {
    if (!data?.commission) {
      toast.error("Dados da comissão não encontrados");
      return;
    }

    try {
      setLoading(true);
      const numericDiscount = parseFloat(
        globalDiscount.replace(/[^\d,]/g, "").replace(",", ".")
      );

      const { ok, data: responseData } = await updateCommissionConfigData(
        data.commission.uuid,
        {
          name: data.commission.name,
          start_date: data.commission.start_date,
          end_date: data.commission.end_date,
          commission_type_id: data.commission.commission_type_id,
          ranges:
            data.commission.commission_ranges?.map((range) => ({
              initial_value: Number(range.initial_value),
              final_value: Number(range.final_value),
              values:
                range.commission_range_values?.map((value) => ({
                  goal_result: value.goal_result,
                  percentage: Number(value.percentage),
                })) || [],
            })) || [],
          sellers: data.commission.sellers?.map((seller) => seller.id) || [],
          manual_discount_value: numericDiscount,
        }
      );

      if (ok) {
        toast.success(
          responseData?.message || "Desconto aplicado com sucesso!"
        );

        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const columns: SortableColumn<CommissionAccompanimentItem>[] = [
    {
      key: "seller_name",
      header: "Vendedor",
      sortable: true,
    },
    {
      key: "commission_without_discount",
      header: "Comissão Sem Desconto",
      align: "right",
      sortable: true,
      render: (row) => formatCurrency(row.commission_without_discount),
    },
    {
      key: "participation_percent",
      header: "Participação (%)",
      align: "right",
      sortable: true,
      render: (row) => formatPercent(row.participation_percent),
    },
    {
      key: "discount",
      header: "Desconto",
      align: "right",
      sortable: true,
      cellClassName: "font-medium text-red-600",
      render: (row) => formatCurrency(row.discount),
    },
    {
      key: "commission_final",
      header: "Comissão Final",
      align: "right",
      sortable: true,
      cellClassName: "font-medium bg-red-50 dark:bg-red-900/20",
      render: (row) => {
        const value = formatCurrency(Math.abs(row.commission_final));
        const className =
          row.commission_final < 0
            ? "text-red-600 dark:text-red-400"
            : "text-green-600 dark:text-green-400";
        return (
          <span className={className}>
            {row.commission_final < 0 ? `- ${value}` : value}
          </span>
        );
      },
    },
  ];

  const handleExportCSV = () => {
    exportToCSV(
      data,
      `comissao-${data.commission_name.toLowerCase().replace(/\s+/g, "-")}`
    );
  };

  const footerContent = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-2">
        <div className="border rounded-md p-3">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Comissão Total
          </div>
          <div className="text-lg font-semibold mt-1">
            {formatCurrency(data.summary.commission_total)}
          </div>
        </div>

        <div className="border rounded-md p-3">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Desconto Total
          </div>
          <div className="text-lg font-semibold mt-1 text-red-600">
            {formatCurrency(data.summary.total_discount)}
          </div>
        </div>

        <div className="border rounded-md p-3">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Porcentagem de Desconto
          </div>
          <div className="text-lg font-semibold mt-1">
            {formatPercent(data.summary.percent_discount)}
          </div>
        </div>

        <div className="border rounded-md p-3 bg-red-50 dark:bg-red-900/20">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Comissão Final Total
          </div>
          <div className="text-lg font-semibold mt-1">
            <span
              className={
                data.summary.commission_final_total < 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-green-600 dark:text-green-400"
              }
            >
              {data.summary.commission_final_total < 0 ? "- " : ""}
              {formatCurrency(Math.abs(data.summary.commission_final_total))}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Valor a Descontar:
          </span>
          <Input
            typeMask="currency"
            value={globalDiscount}
            onChange={handleDiscountChange}
            className="w-32 text-right"
          />
        </div>
        <Button onClick={handleApplyDiscount} className="bg-primary hover:bg-primary/80" disabled={loading}>
          {loading ? "Aplicando..." : "Aplicar Desconto"}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white dark:bg-zinc-800 rounded-md p-4 shadow-sm flex justify-between items-center">
        <h3 className="text-xl font-semibold">{data.commission_name}</h3>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      <SortableTable
        data={data.data}
        columns={columns}
        keyExtractor={(item) => item.seller_id}
        defaultSortKey="seller_name"
        defaultSortDirection="asc"
        footerContent={footerContent}
      />
    </div>
  );
};
